import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Select,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useRef, useState } from "react";
import { FaCopy, FaEye, FaEyeSlash, FaKey, FaTrash } from "react-icons/fa";
import {
  bffApi,
  EntraConnectionDto,
  EntraGroupDto,
  OrganizationRoleType,
  OrgUserInviteRole,
  useDeleteEntraConnectionMutation,
  useDeleteEntraGroupMappingMutation,
  useSaveEntraGroupMappingMutation,
  useUpdateEntraConnectionMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { hasEverSynced } from "./entraSyncState";
import { EntraGroupTable } from "./EntraGroupTable";
import { EntraTenantStatus } from "./EntraTenantStatus";
import { useSelectableInviteRoles } from "./useSelectableInviteRoles";

interface Props {
  organizationId: string;
  connection: EntraConnectionDto;
  departments: { id: string; name: string }[];
}

export const EntraTenantCard = ({
  organizationId,
  connection,
  departments,
}: Props) => {
  const toast = useToast();
  const { triggerSoft } = useApiError();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const inviteRoles = useSelectableInviteRoles();
  const [isKeyVisible, setIsKeyVisible] = useState(false);

  const [getKey, { data: key, isFetching: isLoadingKey }] =
    bffApi.endpoints.getTokenForConnection.useLazyQuery();
  const [saveMapping, { isLoading: isSaving }] =
    useSaveEntraGroupMappingMutation();
  const [deleteMapping, { isLoading: isRemoving }] =
    useDeleteEntraGroupMappingMutation();
  const [updateConnection, { isLoading: isUpdating }] =
    useUpdateEntraConnectionMutation();
  const [disconnect, { isLoading: isDisconnecting }] =
    useDeleteEntraConnectionMutation();

  const isBusy = isSaving || isRemoving || isUpdating || isDisconnecting;
  const synced = hasEverSynced(connection);
  const affected = connection.groups
    .filter((group) => group.mapping)
    .reduce((total, group) => total + group.memberCount, 0);
  const setRole = async (
    group: EntraGroupDto,
    role: OrganizationRoleType,
    departmentId: string | null,
  ): Promise<boolean> => {
    try {
      await saveMapping({
        orgId: organizationId,
        connectionId: connection.id,
        saveEntraGroupMappingRequest: {
          userGroupId: group.userGroupId,
          role,
          departmentId,
        },
      }).unwrap();
      return true;
    } catch (e) {
      triggerSoft(e);
      return false;
    }
  };

  const removeMapping = async (mappingId: string) => {
    try {
      await deleteMapping({
        orgId: organizationId,
        connectionId: connection.id,
        mappingId,
      }).unwrap();
    } catch (e) {
      triggerSoft(e);
    }
  };

  const setDefaultRole = async (defaultRole: OrgUserInviteRole) => {
    try {
      await updateConnection({
        orgId: organizationId,
        connectionId: connection.id,
        updateEntraConnectionRequest: { defaultRole },
      }).unwrap();
    } catch (e) {
      triggerSoft(e);
    }
  };

  const removeTenant = async () => {
    try {
      await disconnect({
        orgId: organizationId,
        connectionId: connection.id,
      }).unwrap();
    } catch (e) {
      triggerSoft(e);
    }
  };

  return (
    <Box
      mt="5"
      width="full"
      rounded="lg"
      border="1px solid"
      borderColor="smBorder"
      overflow="hidden"
    >
      <Flex
        alignItems="center"
        gap="3"
        flexWrap="wrap"
        px="5"
        py="3"
        backgroundColor="smBackgroundSecondary"
        borderBottom="1px solid"
        borderColor="smBorder"
      >
        <Text fontWeight="semibold">{t("Entra tenant")}</Text>
        <Text fontSize="xs" fontFamily="mono" color="smMuted">
          {connection.tenantId}
        </Text>
        <Badge colorScheme={synced ? "teal" : "gray"}>
          {synced ? t("Connected") : t("Waiting for Entra ID")}
        </Badge>
        <Flex flex="1" />
        <IconButton
          aria-label={t("Disconnect this tenant")}
          icon={<FaTrash />}
          size="sm"
          variant="ghost"
          colorScheme="red"
          isDisabled={isBusy}
          onClick={onOpen}
        />
      </Flex>

      <Flex px="5" py="4" gap="10" flexWrap="wrap" alignItems="flex-end">
        <FormControl maxW="xs">
          <FormLabel fontSize="xs" color="smMuted" mb="1">
            {t("Default role for Entra ID users")}
          </FormLabel>
          <Select
            size="sm"
            isDisabled={isBusy}
            onWheel={(e) => e.currentTarget.blur()}
            value={connection.jitDefaultRole}
            onChange={(e) =>
              void setDefaultRole(e.target.value as OrgUserInviteRole)
            }
          >
            {inviteRoles.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl maxW="md">
          <FormLabel fontSize="xs" color="smMuted" mb="1">
            {t("Provisioning key")}
          </FormLabel>
          <Flex gap="2" alignItems="center">
            {!key && (
              <Button
                size="sm"
                variant="outline"
                colorScheme="teal"
                leftIcon={<FaKey />}
                isDisabled={isLoadingKey}
                onClick={() => getKey({ connectionId: connection.id })}
              >
                {isLoadingKey ? <Spinner size="sm" /> : t("Get Secret")}
              </Button>
            )}
            {key && (
              <>
                <Input
                  size="sm"
                  isReadOnly
                  fontFamily="mono"
                  type={isKeyVisible ? "text" : "password"}
                  value={key.token}
                />
                <IconButton
                  aria-label={t("Copy the provisioning key")}
                  icon={<FaCopy />}
                  size="sm"
                  variant="ghost"
                  colorScheme="teal"
                  onClick={() => {
                    void navigator.clipboard.writeText(key.token);
                    toast({
                      status: "success",
                      description: t("Secret copied to the clip board."),
                    });
                  }}
                />
                <IconButton
                  aria-label={
                    isKeyVisible
                      ? t("Hide the provisioning key")
                      : t("Show the provisioning key")
                  }
                  icon={isKeyVisible ? <FaEyeSlash /> : <FaEye />}
                  size="sm"
                  variant="ghost"
                  colorScheme="teal"
                  onClick={() => setIsKeyVisible(!isKeyVisible)}
                />
              </>
            )}
          </Flex>
        </FormControl>
      </Flex>

      <Flex px="5" pb="2">
        <EntraTenantStatus
          organizationId={organizationId}
          connection={connection}
        />
      </Flex>

      <Divider borderColor="smBorderSubtle" />

      <EntraGroupTable
        groups={connection.groups}
        departments={departments}
        isBusy={isBusy}
        onSetRole={setRole}
        onRemove={(mappingId) => void removeMapping(mappingId)}
      />

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t("Disconnect this tenant")}
            </AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              {t(
                "{{people}} people get their access from this tenant's groups and will lose it. Entra ID keeps the groups; only what they give here goes away",
                { people: affected },
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                size="sm"
                variant="ghost"
                onClick={onClose}
              >
                {t("Cancel")}
              </Button>
              <Button
                size="sm"
                colorScheme="red"
                ml="3"
                isDisabled={isBusy}
                onClick={() => {
                  onClose();
                  void removeTenant();
                }}
              >
                {t("Disconnect")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
